import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useNews, NewsItem } from '../context/NewsContext';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Plus, Edit2, Trash2, X, AlertCircle, CheckCircle, Calendar, LogOut } from 'lucide-react';
import '../news.css';

interface FormData {
  title: string;
  category: NewsItem['category'];
  description: string;
  content: string;
  urgent: boolean;
  date: string;
  details: {
    lastDate?: string;
    availableSeats?: number;
    collegeName?: string;
    link?: string;
  };
}

const AdminNewsPage: React.FC = () => {
  const navigate = useNavigate();
  const { news, addNews, updateNews, deleteNews } = useNews();
  const { logout, adminUsername } = useAdminAuth();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const initialFormData: FormData = {
    title: '',
    category: 'GENERAL',
    description: '',
    content: '',
    urgent: false,
    date: new Date().toISOString().split('T')[0],
    details: {
      lastDate: '',
      availableSeats: undefined,
      collegeName: '',
      link: ''
    }
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);

  // Filter news based on search
  const filteredNews = useMemo(() => {
    return news.filter(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, news]);

  const handleEdit = (item: NewsItem) => {
    setFormData({
      title: item.title,
      category: item.category,
      description: item.description,
      content: item.content,
      urgent: item.urgent,
      date: item.date,
      details: item.details || {}
    });
    setEditingId(item.id);
    setIsFormOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim() || !formData.content.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (editingId) {
      updateNews(editingId, {
        ...formData,
        details: {
          lastDate: formData.details.lastDate || undefined,
          availableSeats: formData.details.availableSeats,
          collegeName: formData.details.collegeName || undefined,
          link: formData.details.link || undefined
        }
      });
      setSuccessMessage('News updated successfully!');
    } else {
      addNews({
        ...formData,
        details: {
          lastDate: formData.details.lastDate || undefined,
          availableSeats: formData.details.availableSeats,
          collegeName: formData.details.collegeName || undefined,
          link: formData.details.link || undefined
        }
      });
      setSuccessMessage('News added successfully!');
    }

    setIsFormOpen(false);
    setEditingId(null);
    setFormData(initialFormData);

    // Clear success message after 3 seconds
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteConfirm = (id: string) => {
    deleteNews(id);
    setDeleteConfirm(null);
    setSuccessMessage('News deleted successfully!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  return (
    <div className="admin-news-container">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex justify-between items-start"
        >
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Manage News</h1>
            <p className="text-gray-600">Add, edit, or delete latest news and updates</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleLogout}
            title="Logout from admin panel"
            className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </motion.button>
        </motion.div>

        {/* Success Message */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg flex items-center gap-3"
            >
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700">{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search and Add Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          <input
            type="text"
            placeholder="Search news..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 focus:border-purple-500 focus:outline-none"
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setFormData(initialFormData);
              setEditingId(null);
              setIsFormOpen(true);
            }}
            className="btn-gradient text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 whitespace-nowrap"
          >
            <Plus className="w-5 h-5" />
            Add New News
          </motion.button>
        </motion.div>

        {/* Form Modal */}
        <AnimatePresence>
          {isFormOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={handleCloseForm}
            >
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="modal-content max-w-2xl"
              >
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingId ? 'Edit News' : 'Add New News'}
                  </h2>
                  <button
                    onClick={handleCloseForm}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="form-input w-full px-4 py-2 rounded-lg"
                        placeholder="Enter news title"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        required
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            category: e.target.value as NewsItem['category']
                          })
                        }
                        className="form-select w-full px-4 py-2 rounded-lg"
                      >
                        <option value="ALL_UPDATES">All Updates</option>
                        <option value="GENERAL">General</option>
                        <option value="TNEA">TNEA</option>
                        <option value="TNLEA">TNLEA</option>
                        <option value="TN_MEDICAL">TN Medical</option>
                        <option value="TN_AYUSH">TN AYUSH</option>
                        <option value="TNDALU">TNDALU</option>
                        <option value="TANUVAS">TANUVAS</option>
                        <option value="SCHOLARSHIPS">Scholarships</option>
                      </select>
                    </div>

                    {/* Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="form-input w-full px-4 py-2 rounded-lg"
                      />
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description (Short Summary) *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({ ...formData, description: e.target.value })
                        }
                        className="form-input w-full px-4 py-2 rounded-lg"
                        placeholder="Brief description for preview"
                        maxLength={150}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.description.length}/150 characters
                      </p>
                    </div>

                    {/* Content */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Content *
                      </label>
                      <textarea
                        required
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        className="form-input w-full px-4 py-2 rounded-lg h-32 resize-none"
                        placeholder="Full news content"
                      />
                    </div>

                    {/* Urgent Toggle */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="urgent"
                        checked={formData.urgent}
                        onChange={(e) =>
                          setFormData({ ...formData, urgent: e.target.checked })
                        }
                        className="w-4 h-4"
                      />
                      <label htmlFor="urgent" className="text-sm font-medium text-gray-700">
                        Mark as Urgent
                      </label>
                    </div>

                    {/* Last Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Date (if applicable)
                      </label>
                      <input
                        type="date"
                        value={formData.details.lastDate || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            details: {
                              ...formData.details,
                              lastDate: e.target.value
                            }
                          })
                        }
                        className="form-input w-full px-4 py-2 rounded-lg"
                      />
                    </div>

                    {/* Available Seats */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Seats
                      </label>
                      <input
                        type="number"
                        value={formData.details.availableSeats || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            details: {
                              ...formData.details,
                              availableSeats: e.target.value ? parseInt(e.target.value) : undefined
                            }
                          })
                        }
                        className="form-input w-full px-4 py-2 rounded-lg"
                        placeholder="e.g., 5000"
                      />
                    </div>

                    {/* College Name */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        College Name
                      </label>
                      <input
                        type="text"
                        value={formData.details.collegeName || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            details: {
                              ...formData.details,
                              collegeName: e.target.value
                            }
                          })
                        }
                        className="form-input w-full px-4 py-2 rounded-lg"
                        placeholder="College name (optional)"
                      />
                    </div>

                    {/* Link */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reference Link
                      </label>
                      <input
                        type="url"
                        value={formData.details.link || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            details: {
                              ...formData.details,
                              link: e.target.value
                            }
                          })
                        }
                        className="form-input w-full px-4 py-2 rounded-lg"
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-4 mt-6">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      className="btn-gradient text-white px-6 py-2 rounded-lg font-semibold flex-1"
                    >
                      {editingId ? 'Update News' : 'Add News'}
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={handleCloseForm}
                      className="bg-gray-300 text-gray-800 px-6 py-2 rounded-lg font-semibold flex-1"
                    >
                      Cancel
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* News List */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="admin-news-list"
        >
          <AnimatePresence>
            {filteredNews.length === 0 ? (
              <motion.div
                key="no-news"
                variants={itemVariants}
                className="col-span-full text-center py-12"
              >
                <div className="text-gray-400 text-lg">No news found</div>
              </motion.div>
            ) : (
              filteredNews.map((item) => (
                <motion.div
                  key={item.id}
                  variants={itemVariants}
                  className="admin-news-card"
                >
                  {/* Urgent Badge */}
                  {item.urgent && (
                    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-red-200">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-bold text-red-600">URGENT</span>
                    </div>
                  )}

                  {/* Category */}
                  <div className="mb-2">
                    <span className={`category-badge ${
                      item.category === 'ALL_UPDATES'
                        ? 'category-all'
                        : item.category === 'GENERAL'
                        ? 'category-general'
                        : item.category === 'TNEA'
                        ? 'category-tnea'
                        : item.category === 'TNLEA'
                        ? 'category-tnlea'
                        : item.category === 'TN_MEDICAL'
                        ? 'category-tn-medical'
                        : item.category === 'TN_AYUSH'
                        ? 'category-tn-ayush'
                        : item.category === 'TNDALU'
                        ? 'category-tndalu'
                        : item.category === 'TANUVAS'
                        ? 'category-tanuvas'
                        : item.category === 'SCHOLARSHIPS'
                        ? 'category-scholarships'
                        : 'category-general'
                    }`}>
                      {item.category}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-gray-800 mb-2 line-clamp-2">{item.title}</h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{item.description}</p>

                  {/* Date */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <Calendar className="w-3 h-3" />
                    {new Date(item.createdAt).toLocaleDateString('en-IN')}
                  </div>

                  {/* Action Buttons */}
                  <div className="admin-action-buttons">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleEdit(item)}
                      className="edit-btn"
                    >
                      <Edit2 className="w-4 h-4 inline mr-1" />
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setDeleteConfirm(item.id)}
                      className="delete-btn"
                    >
                      <Trash2 className="w-4 h-4 inline mr-1" />
                      Delete
                    </motion.button>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="modal-content max-w-sm"
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4">Confirm Delete</h2>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this news item?</p>
              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleDeleteConfirm(deleteConfirm)}
                  className="btn-danger text-white px-4 py-2 rounded-lg font-semibold flex-1"
                >
                  Delete
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setDeleteConfirm(null)}
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold flex-1"
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminNewsPage;
